import React, { useState, useEffect } from 'react';
import './ScriptLogViewer.css';
import { curDevopsApi } from '../_config.js';

const ScriptLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailedLog, setDetailedLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scriptFilter, setScriptFilter] = useState('');
  const [uniqueScripts, setUniqueScripts] = useState([]);

  // Fetch script logs
  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `${curDevopsApi}/api/server-scripts/script-logs?limit=50`;
      if (scriptFilter) {
        url += `&script_name=${encodeURIComponent(scriptFilter)}`;
      }
      
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Logs response:', data);
      
      // Handle both array format and object with logs property
      const logsArray = Array.isArray(data) ? data : Array.isArray(data.logs) ? data.logs : [];
      setLogs(logsArray);
      
      // Extract unique script names for filtering
      const scripts = [...new Set(logsArray.map(log => log.script_name))];
      setUniqueScripts(scripts);
      
    } catch (err) {
      setError(`Failed to fetch logs: ${err.message}`);
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific log details
  const fetchLogDetails = async (runId) => {
    try {
      const response = await fetch(`${curDevopsApi}/api/server-scripts/script-log/${runId}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setDetailedLog(data);
      
    } catch (err) {
      setError(`Failed to fetch log details: ${err.message}`);
      console.error('Error fetching log details:', err);
    }
  };

  // Load logs on component mount and when filter changes
  useEffect(() => {
    fetchLogs();
  }, [scriptFilter,]);

  // Handle log selection
  const handleLogSelect = (log) => {
    setSelectedLog(log);
    fetchLogDetails(log.run_id);
  };

  // Format duration for display
  const formatDuration = (seconds) => {
    if (seconds < 60) {
      return `${seconds.toFixed(2)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  };

  // Format date for display
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  // Get status class based on exit code
  const getStatusClass = (exitCode) => {
    if (exitCode === 0) return 'success';
    if (exitCode === -1) return 'canceled';
    return 'error';
  };

  return (
    <div className="script-log-viewer">
      <div className="log-controls">
        <h2>Script Execution Logs</h2>
        
        <div className="controls-row">
          <div className="filter-controls">
            <select 
              value={scriptFilter} 
              onChange={(e) => setScriptFilter(e.target.value)}
              className="script-filter"
            >
              <option value="">All Scripts</option>
              {uniqueScripts.map(script => (
                <option key={script} value={script}>{script}</option>
              ))}
            </select>
          </div>
          
          <button 
            className="refresh-button" 
            onClick={fetchLogs} 
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="logs-container">
        <div className="logs-list">
          <div className="list-header">
            <div className="header-script">Script</div>
            <div className="header-time">Time</div>
            <div className="header-duration">Duration</div>
            <div className="header-status">Status</div>
          </div>
          
          <div className="logs-scrollable">
            {logs.length === 0 ? (
              <div className="no-logs-message">
                {loading ? 'Loading logs...' : 'No logs found'}
              </div>
            ) : (
              logs.map(log => (
                <div 
                  key={log.run_id} 
                  className={`log-item ${selectedLog?.run_id === log.run_id ? 'selected' : ''}`}
                  onClick={() => handleLogSelect(log)}
                >
                  <div className="item-script">{log.script_name}</div>
                  <div className="item-time">{formatDate(log.start_time)}</div>
                  <div className="item-duration">{formatDuration(log.duration_seconds)}</div>
                  <div className={`item-status status-${getStatusClass(log.exit_code)}`}>
                    {log.exit_code === 0 ? 'Success' : log.exit_code === -1 ? 'Canceled' : `Failed (${log.exit_code})`}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        <div className="log-details">
          {selectedLog ? (
            detailedLog ? (
              <div className="detail-content">
                <h3>Execution Details</h3>
                <div className="detail-header">
                  <div className="detail-item">
                    <span className="detail-label">Script:</span>
                    <span className="detail-value">{detailedLog.script_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Start Time:</span>
                    <span className="detail-value">{formatDate(detailedLog.start_time)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Duration:</span>
                    <span className="detail-value">{formatDuration(detailedLog.duration_seconds)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={`detail-value status-${getStatusClass(detailedLog.exit_code)}`}>
                      {detailedLog.exit_code === 0 ? 'Success' : detailedLog.exit_code === -1 ? 'Canceled' : `Failed (${detailedLog.exit_code})`}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Run ID:</span>
                    <span className="detail-value run-id">{detailedLog.run_id}</span>
                  </div>
                </div>
                
                <h4>Output</h4>
                <div className="output-container">
                  {detailedLog.output && detailedLog.output.map((line, index) => (
                    <div key={index} className="output-line">
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="loading-details">Loading log details...</div>
            )
          ) : (
            <div className="no-selection">
              <p>Select a log to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScriptLogViewer;